import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateTimePicker, formatDateTime } from "@/components/DateTimePicker";
import { studioApi, errMessage } from "@/lib/studioApi";

/** Console header control: shows the schedule and opens a dialog to edit title + scheduled time. */
export function EditShowDialog({ show, onSaved }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState("");
  const [saving, setSaving] = useState(false);

  const openDialog = () => {
    setTitle(show?.title || "");
    setWhen(show?.scheduled_for || "");
    setOpen(true);
  };

  const save = async () => {
    const t = title.trim();
    if (!t) { toast.error("Give the show a title"); return; }
    setSaving(true);
    try {
      const res = await studioApi.patchShow(show.show_id, { title: t, scheduled_for: when || "" });
      onSaved?.(res);
      toast.success("Show details saved");
      setOpen(false);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="group inline-flex min-w-0 items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-white/5"
        title="Edit title & scheduled time"
        data-testid="edit-show-button"
      >
        <span className="truncate text-base font-semibold" data-testid="console-title">{show?.title || "Loading…"}</span>
        {show?.scheduled_for && (
          <span className="hidden truncate text-xs text-zinc-400 sm:inline" data-testid="console-scheduled">· {formatDateTime(show.scheduled_for)}</span>
        )}
        <Pencil className="h-3.5 w-3.5 shrink-0 text-zinc-500 transition-colors group-hover:text-[#e6a25f]" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="edit-show-dialog" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Edit show</DialogTitle>
            <DialogDescription>Rename the show or move its scheduled start. Guests and the Hub update instantly.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground" htmlFor="edit-show-title">Title</label>
              <Input id="edit-show-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} className="mt-1" data-testid="edit-show-title" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Scheduled for</label>
              <div className="mt-1">
                <DateTimePicker value={when} onChange={setWhen} placeholder="Pick a date & start time" testId="edit-show-when" />
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">Shown in your local time zone. Clear it to leave the show unscheduled.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="edit-show-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="edit-show-save">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default EditShowDialog;
